import { useState } from "react";
import { Link } from "react-router-dom";
import LayoutWide from "../layouts/LayoutWide.jsx";
import Spinner from "../components/Spinner.jsx";
import GrowCard from "../components/GrowCard.jsx";
import PageHeader from "../components/ui/PageHeader.jsx";
import { useGrowsQuery } from "../hooks/useGrowsQuery.js";
import { useQuery } from "@tanstack/react-query";

const API = import.meta.env.VITE_API_BASE_URL;

function useDashboardQuery() {
  return useQuery({
    queryKey: ["dashboard"],
    queryFn: async () => {
      const res = await fetch(`${API}dashboard`, { credentials: "include" });
      if (!res.ok) throw new Error("dashboard fetch failed");
      return res.json();
    },
    staleTime: 30_000,
    refetchInterval: 60_000,
  });
}

function SectionHeading({ label, count, open, onToggle }) {
  return (
    <h2 className="mb-3">
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        className="flex items-center gap-1.5 text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide hover:text-gray-700 dark:hover:text-gray-200 transition-colors"
      >
        <svg
          className={`w-3.5 h-3.5 transition-transform ${open ? "rotate-90" : ""}`}
          viewBox="0 0 20 20"
          fill="currentColor"
          aria-hidden="true"
        >
          <path fillRule="evenodd" d="M7.21 14.77a.75.75 0 01.02-1.06L11.168 10 7.23 6.29a.75.75 0 111.04-1.08l4.5 4.25a.75.75 0 010 1.08l-4.5 4.25a.75.75 0 01-1.06-.02z" clipRule="evenodd" />
        </svg>
        {label} ({count})
      </button>
    </h2>
  );
}

export default function Grows() {
  const { data: grows, isLoading, isError } = useGrowsQuery();
  const { data: dashboard } = useDashboardQuery();
  const [showActive, setShowActive] = useState(true);
  const [showCompleted, setShowCompleted] = useState(false);

  if (isLoading) return <LayoutWide title="Grows"><Spinner /></LayoutWide>;
  if (isError) return <LayoutWide title="Grows"><div className="p-4 text-red-600 dark:text-red-400">Failed to load grows.</div></LayoutWide>;

  const active = (grows ?? []).filter(g => g.status === "active");
  const completed = (grows ?? []).filter(g => g.status !== "active");

  function liveDeviceFor(grow) {
    if (!grow.device_id || !dashboard?.devices) return null;
    return dashboard.devices[grow.device_id] ?? null;
  }

  return (
    <LayoutWide title="Grows">
      <PageHeader title="Grows" actionLabel="+ New Grow" actionTo="/grows/new" />

      {active.length === 0 && completed.length === 0 && (
        <div className="text-center py-20 text-gray-400 dark:text-gray-500">
          <div className="text-5xl mb-4">🍄</div>
          <div className="text-lg font-medium text-gray-500 dark:text-gray-400 mb-2">No grows yet</div>
          <div className="text-sm mb-6">Start tracking your first cultivation batch.</div>
          <Link
            to="/grows/new"
            className="text-sm bg-sky-600 text-white rounded px-4 py-2 hover:bg-sky-700 dark:bg-sky-700 dark:hover:bg-sky-600 transition-colors font-medium"
          >
            Start your first grow
          </Link>
        </div>
      )}

      {active.length > 0 && (
        <section className="mb-8">
          <SectionHeading label="Active" count={active.length} open={showActive} onToggle={() => setShowActive(v => !v)} />
          {showActive && (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
              {active.map(grow => (
                <GrowCard key={grow.id} grow={grow} liveDevice={liveDeviceFor(grow)} hostname={liveDeviceFor(grow)?.device?.hostname} />
              ))}
            </div>
          )}
        </section>
      )}

      {completed.length > 0 && (
        <section>
          <SectionHeading label="Completed" count={completed.length} open={showCompleted} onToggle={() => setShowCompleted(v => !v)} />
          {showCompleted && (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
              {completed.map(grow => (
                <GrowCard key={grow.id} grow={grow} liveDevice={liveDeviceFor(grow)} hostname={liveDeviceFor(grow)?.device?.hostname} />
              ))}
            </div>
          )}
        </section>
      )}
    </LayoutWide>
  );
}
